import { ChevronDown, ChevronRight, Scale } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useLocale } from "../lib/i18n";
import type { AuditTrace } from "../lib/types";
import { OverrideStackViewer } from "./OverrideStackViewer";

function formatValue(v: unknown): string {
  if (v === null) return "null";
  if (typeof v === "string") return v;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  if (typeof v === "object" && v !== null && "amount" in v && "currency" in v) {
    const m = v as { amount: string; currency: string };
    return `${m.amount} ${m.currency}`;
  }
  return JSON.stringify(v);
}

function KvTable({ kv }: { kv: Record<string, unknown> }) {
  const entries = Object.entries(kv);
  if (entries.length === 0)
    return <p className="text-xs text-slate-400">(empty)</p>;
  return (
    <table className="w-full text-sm">
      <tbody className="divide-y divide-slate-100">
        {entries.map(([k, v]) => (
          <tr key={k}>
            <td className="w-1/3 py-1.5 ps-3 pe-4 font-mono text-xs text-slate-500">{k}</td>
            <td className="py-1.5 ps-4 pe-3 font-mono text-xs tabular-nums text-slate-800">
              {formatValue(v)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function StepList({ steps }: { steps: AuditTrace["steps"] }) {
  if (steps.length === 0)
    return <p className="text-xs text-slate-400">(no steps recorded)</p>;
  return (
    <ol className="space-y-1 text-xs">
      {steps.map((s, i) => (
        <li key={i} className="flex gap-2">
          <span className="inline-flex h-5 w-12 shrink-0 items-center justify-center rounded bg-slate-100 font-mono text-[10px] text-slate-600">
            {s.kind}
          </span>
          <span className="flex-1 font-mono text-slate-700">{s.description}</span>
        </li>
      ))}
    </ol>
  );
}

function Section({
  title,
  children,
  defaultOpen = false,
}: {
  title: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="rounded border border-slate-200 bg-white">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between px-3 py-2 text-start"
      >
        <span className="text-xs font-semibold uppercase tracking-wider text-slate-600">
          {title}
        </span>
        {open ? (
          <ChevronDown className="h-4 w-4 text-slate-400" />
        ) : (
          <ChevronRight className="h-4 w-4 text-slate-400" />
        )}
      </button>
      {open ? <div className="border-t border-slate-100 px-3 py-3">{children}</div> : null}
    </div>
  );
}

/**
 * @deprecated AuditTraceViewer is superseded by OverrideStackViewer
 * (M12.4) for resolution-path tracing, paired with the
 * TraceableValue primitive (F4) for the click affordance. New
 * code must NOT import this component. Existing M3-era consumers
 * (PayrollPage) continue to render it until a follow-up PR ports
 * them to the new pattern.
 *
 * File deletion is intentionally deferred until a future cleanup
 * PR — one full release cycle after Foundation sprint completion
 * confirms nothing in production hits this path.
 */
export function AuditTraceViewer({
  trace,
  depth = 0,
}: {
  trace: AuditTrace;
  depth?: number;
}) {
  const { t: t2 } = useTranslation();
  if (
    typeof import.meta !== "undefined" &&
    import.meta.env?.DEV &&
    depth === 0
  ) {
    // eslint-disable-next-line no-console
    console.warn(
      "[deprecated] AuditTraceViewer is superseded by OverrideStackViewer + TraceableValue. " +
        "Migrate callers and remove this import in a follow-up PR.",
    );
  }
  const { t } = useLocale();
  return (
    <div className={depth === 0 ? "space-y-3" : "space-y-3 ps-4"}>
      <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-slate-500">
              <Scale className="h-3.5 w-3.5" />
              {t.trace_title}
            </p>
            <p className="mt-1 font-mono text-sm font-semibold text-slate-900">
              {trace.rule}
            </p>
            <p className="mt-0.5 text-xs text-slate-600">{trace.authority}</p>
          </div>
          <div className="text-end">
            <p className="text-[10px] uppercase tracking-wider text-slate-500">
              {t.trace_resolution_date}
            </p>
            <p className="font-mono text-sm tabular-nums text-slate-800">
              {trace.resolution_date}
            </p>
          </div>
        </div>
      </div>

      <Section title={t.trace_inputs}>
        <KvTable kv={trace.inputs} />
      </Section>
      <Section title={t.trace_outputs} defaultOpen>
        <KvTable kv={trace.outputs} />
      </Section>
      {Object.keys(trace.let_bindings).length > 0 ? (
        <Section title="let bindings">
          <KvTable kv={trace.let_bindings} />
        </Section>
      ) : null}
      <Section title={t.trace_steps}>
        <StepList steps={trace.steps} />
      </Section>
      {trace.resolution_paths &&
      Object.keys(trace.resolution_paths).length > 0 ? (
        <Section title={t2("app.ats_override_stacks")}>
          <div className="space-y-3">
            {Object.entries(trace.resolution_paths).map(([componentName, path]) => (
              <OverrideStackViewer
                key={componentName}
                componentName={componentName}
                path={path}
              />
            ))}
          </div>
        </Section>
      ) : null}
      {trace.nested_calls.length > 0 ? (
        <Section title={t.trace_nested_calls}>
          <div className="space-y-3">
            {trace.nested_calls.map((nested, i) => (
              <AuditTraceViewer key={i} trace={nested} depth={depth + 1} />
            ))}
          </div>
        </Section>
      ) : null}
    </div>
  );
}
